import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'src/ui/Button';
import { Select } from 'src/ui/Select';
import { Textarea } from 'src/ui/Textarea';
import { Badge } from 'src/ui/Badge';
import { Spinner } from 'src/ui/Spinner';
import { Tabs } from 'src/ui/Tabs';
import { Icon } from 'src/ui/Icon';
import { useToast } from 'src/ui/Toast';
import { Panel } from 'src/ui/Panel';
import { Dropdown, MenuItem } from 'src/ui/Dropdown';
import { Modal } from 'src/ui/Modal';
import { Alert } from 'src/ui/Alert';
import {
  useListPromptsQuery,
  useCreatePromptVersionMutation,
  useUpdatePromptVersionMutation,
  useActivatePromptVersionMutation,
  useImportPromptsMutation,
  PromptGroup,
  PromptVersionContent,
  PromptExportRow,
  FIELD_GUIDANCE_KEY,
} from 'src/store/matchmakerPromptsApi';

interface PromptPanelProps {
  /** key → versionId currently previewed in the chat. */
  preview: Record<string, number>;
  onTogglePreview: (key: string, versionId: number) => void;
}

const CATEGORIES: { label: string; test: (k: string) => boolean }[] = [
  { label: 'System', test: (k) => k.startsWith('system.') },
  { label: 'Tools', test: (k) => k.startsWith('tool.') },
  { label: 'Summarizer', test: (k) => k.startsWith('summarizer.') },
  { label: 'Simulation', test: (k) => k.startsWith('sim.') },
  { label: 'Fields', test: (k) => k === FIELD_GUIDANCE_KEY },
];

const splitLines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

/** Per-leaf edit form (examples kept as newline strings for editing). */
type FieldDraft = Record<
  string,
  { en: string; cn: string; examplesEn: string; examplesCn: string }
>;

const PromptPanel = ({ preview, onTogglePreview }: PromptPanelProps) => {
  const toast = useToast();
  const { data: groups = [], isLoading } = useListPromptsQuery();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [createVersion, _createStatus] = useCreatePromptVersionMutation();
  const [updateVersion, updateStatus] = useUpdatePromptVersionMutation();
  const [activateVersion, activateStatus] = useActivatePromptVersionMutation();
  const [importPrompts, importStatus] = useImportPromptsMutation();

  // Sync Prompt DB — download / upload of the whole prompt table as JSON.
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [pendingImport, setPendingImport] = useState<{
    rows: PromptExportRow[];
    fileName: string;
  } | null>(null);

  const [selectedKey, setSelectedKey] = useState('');
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
    null
  );
  const [buf, setBuf] = useState({ en: '', cn: '' });
  const [fieldDraft, setFieldDraft] = useState<FieldDraft>({});
  const [selectedLeaf, setSelectedLeaf] = useState('');
  // EN / 中文 pill tab for the editor area
  const [lang, setLang] = useState<'en' | 'cn'>('en');

  const group: PromptGroup | undefined = groups.find(
    (g) => g.key === selectedKey
  );
  const version = group?.versions.find((v) => v.id === selectedVersionId);
  const isFieldGuidance = selectedKey === FIELD_GUIDANCE_KEY;

  useEffect(() => {
    if (!selectedKey && groups.length) setSelectedKey(groups[0].key);
  }, [groups, selectedKey]);

  useEffect(() => {
    if (!group) return;
    const active = group.versions.find((v) => v.isActive) ?? group.versions[0];
    setSelectedVersionId(active?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, groups.length]);

  useEffect(() => {
    if (!version) return;
    if (version.key === FIELD_GUIDANCE_KEY) {
      const draft: FieldDraft = {};
      const data = version.data || {};
      for (const leaf of Object.keys(data)) {
        draft[leaf] = {
          en: data[leaf].en || '',
          cn: data[leaf].cn || '',
          examplesEn: (data[leaf].examplesEn || []).join('\n'),
          examplesCn: (data[leaf].examplesCn || []).join('\n'),
        };
      }
      setFieldDraft(draft);
      setSelectedLeaf(Object.keys(draft).sort()[0] || '');
    } else {
      setBuf({ en: version.en || '', cn: version.cn || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersionId]);

  const groupedOptions = useMemo(
    () =>
      CATEGORIES.map((cat) => ({
        label: cat.label,
        keys: groups
          .map((g) => g.key)
          .filter(cat.test)
          .sort(),
      })).filter((c) => c.keys.length),
    [groups]
  );

  const buildContent = (): PromptVersionContent => {
    if (isFieldGuidance) {
      const data: NonNullable<PromptVersionContent['data']> = {};
      for (const leaf of Object.keys(fieldDraft)) {
        const d = fieldDraft[leaf];
        data[leaf] = {
          en: d.en,
          cn: d.cn,
          examplesEn: splitLines(d.examplesEn),
          examplesCn: splitLines(d.examplesCn),
        };
      }
      return { data };
    }
    return { en: buf.en, cn: buf.cn };
  };

  const err = (title: string, e: any) =>
    toast({
      type: 'error',
      title,
      message: e?.data?.message || e?.message || e?.error,
    });

  const onSave = async () => {
    if (!selectedVersionId) return;
    try {
      await updateVersion({
        id: selectedVersionId,
        ...buildContent(),
      }).unwrap();
      toast({ type: 'success', title: 'Saved' });
    } catch (e) {
      err('Save failed', e);
    }
  };

  const onNewVersion = async () => {
    if (!selectedKey) return;
    try {
      const created = await createVersion({
        key: selectedKey,
        ...buildContent(),
        note: 'draft',
      }).unwrap();
      setSelectedVersionId(created.id);
      toast({ type: 'success', title: `Created v${created.version}` });
    } catch (e) {
      err('Create failed', e);
    }
  };

  const onActivate = async () => {
    if (!selectedVersionId) return;
    try {
      await activateVersion(selectedVersionId).unwrap();
      toast({ type: 'success', title: 'Activated (production)' });
    } catch (e) {
      err('Activate failed', e);
    }
  };

  // ---- Sync Prompt DB (download / upload the whole table as JSON) ----

  const onDownloadPrompts = () => {
    const rows: PromptExportRow[] = groups.flatMap((g) =>
      g.versions.map((v) => ({
        key: v.key,
        version: v.version,
        en: v.en,
        cn: v.cn,
        data: v.data,
        isActive: v.isActive,
        note: v.note,
      }))
    );
    const blob = new Blob([JSON.stringify({ prompts: rows }, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matchmaker-prompts-${new Date()
      .toISOString()
      .slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const onUploadFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      toast({
        type: 'error',
        title: 'Invalid file',
        message: 'Could not parse JSON.',
      });
      return;
    }
    const rows = Array.isArray(parsed)
      ? parsed
      : (parsed as { prompts?: unknown })?.prompts;
    if (!Array.isArray(rows) || rows.length === 0) {
      toast({
        type: 'error',
        title: 'Invalid prompts file',
        message:
          'Expected a JSON array of prompts or an object with a "prompts" array.',
      });
      return;
    }
    setPendingImport({ rows: rows as PromptExportRow[], fileName: file.name });
  };

  const runImport = async () => {
    if (!pendingImport) return;
    try {
      const res = await importPrompts({ prompts: pendingImport.rows }).unwrap();
      toast({
        type: 'success',
        title: 'Prompt DB overwritten',
        message: `${res.count} row(s) across ${res.keys} key(s) imported.`,
      });
      // Force a clean re-selection against the refetched groups.
      setSelectedKey('');
      setSelectedVersionId(null);
      setPendingImport(null);
    } catch (e) {
      err('Import failed', e);
    }
  };

  const isPreviewing =
    !!selectedVersionId && preview[selectedKey] === selectedVersionId;

  const leaf = fieldDraft[selectedLeaf];
  const setLeafField = (k: keyof FieldDraft[string], val: string) =>
    setFieldDraft((prev) => ({
      ...prev,
      [selectedLeaf]: { ...prev[selectedLeaf], [k]: val },
    }));

  // Sync Prompt DB — download the whole prompt table as JSON, or upload a JSON
  // to overwrite it.
  const headerRight = (
    <>
      <Dropdown
        align="right"
        width={200}
        trigger={
          <Button
            variant="secondary"
            size="sm"
            leftIcon="database"
            rightIcon="chevron-down"
            loading={importStatus.isLoading}
          >
            Sync Prompt DB
          </Button>
        }
      >
        <MenuItem icon="download" onClick={onDownloadPrompts}>
          Download prompts
        </MenuItem>
        <MenuItem icon="upload" onClick={() => uploadInputRef.current?.click()}>
          Upload prompts
        </MenuItem>
      </Dropdown>
      <input
        type="file"
        ref={uploadInputRef}
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={onUploadFileChange}
      />
    </>
  );

  // Unique key forces the editor textarea to re-mount when the selection changes
  const editorKey =
    selectedKey +
    '/' +
    selectedVersionId +
    '/' +
    (selectedLeaf || '') +
    '/' +
    lang;

  // Render editor area for the current language selection
  const renderEditor = () => {
    if (isFieldGuidance) {
      if (!leaf) return null;
      const exKey = lang === 'en' ? 'examplesEn' : 'examplesCn';
      const descKey = lang as 'en' | 'cn';
      return (
        <>
          <div className="col gap-1" style={{ flexShrink: 0 }}>
            <label
              style={{
                fontSize: 'var(--fs-xs)',
                fontWeight: 500,
                color: 'var(--text-secondary)',
              }}
            >
              {lang === 'en' ? 'Description' : '说明'}
            </label>
            <Textarea
              style={{ minHeight: 120, resize: 'none' }}
              value={leaf[descKey]}
              onChange={(e) => setLeafField(descKey, e.target.value)}
            />
          </div>
          <div
            className="col gap-1"
            style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
          >
            <label
              style={{
                fontSize: 'var(--fs-xs)',
                fontWeight: 500,
                color: 'var(--text-secondary)',
              }}
            >
              {lang === 'en' ? 'Examples' : '示例'}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 'var(--fs-2xs)',
                  color: 'var(--text-muted)',
                }}
              >
                {lang === 'en' ? 'one per line' : '每行一个'}
              </span>
            </label>
            <Textarea
              className="mono"
              style={{ flex: 1, minHeight: 80, resize: 'none' }}
              value={leaf[exKey]}
              onChange={(e) => setLeafField(exKey, e.target.value)}
            />
          </div>
        </>
      );
    }

    // Regular prompt: single full-height editor for the selected language
    return (
      <div
        className="col gap-1"
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <Textarea
          className="mono"
          style={{ flex: 1, minHeight: 120, resize: 'none' }}
          value={lang === 'en' ? buf.en : buf.cn}
          onChange={(e) => setBuf({ ...buf, [lang]: e.target.value })}
        />
      </div>
    );
  };

  // Panel footer: Save + Preview in chat buttons
  const footer = (
    <div className="row gap-2">
      <Button
        variant="primary"
        leftIcon="save"
        loading={updateStatus.isLoading}
        className="grow"
        onClick={onSave}
      >
        Save changes
      </Button>
      <button
        className="btn"
        onClick={() =>
          selectedVersionId && onTogglePreview(selectedKey, selectedVersionId)
        }
        disabled={!selectedVersionId}
        title="Use this prompt's preview version in the live chat"
        style={
          isPreviewing
            ? {
                background: 'var(--purple-solid)',
                color: '#fff',
                borderColor: 'transparent',
              }
            : {
                background: 'transparent',
                color: 'var(--purple-text)',
                borderColor: 'var(--purple-border)',
              }
        }
      >
        <Icon name={isPreviewing ? 'eye' : 'play-circle'} size={16} />
        {isPreviewing ? 'Previewing' : 'Preview in chat'}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <Panel
        title="Matchmaker prompts"
        icon="list-tree"
        headerRight={headerRight}
        className="grow"
      >
        <div
          className="col"
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            height: '100%',
            color: 'var(--text-muted)',
          }}
        >
          <Spinner size="lg" />
          <div style={{ fontSize: 'var(--fs-sm)' }}>Loading prompts…</div>
        </div>
      </Panel>
    );
  }

  return (
    <>
      <Panel
        title="Matchmaker prompts"
        icon="list-tree"
        headerRight={headerRight}
        className="grow"
        footer={footer}
        bodyStyle={{
          padding: '10px 14px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          overflow: 'hidden',
        }}
      >
        <div className="col gap-3" style={{ height: '100%' }}>
          {/* Prompt select (grouped by category) */}
          <div>
            <label
              style={{
                fontSize: 'var(--fs-2xs)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Prompt
            </label>
            <Select
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
            >
              {groupedOptions.map((cat) => (
                <optgroup key={cat.label} label={cat.label}>
                  {cat.keys.map((k) => (
                    <option key={k} value={k}>
                      {k === FIELD_GUIDANCE_KEY
                        ? 'field guidance (all fields)'
                        : k}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </div>

          {/* Field select — field-guidance mode only */}
          {isFieldGuidance && (
            <div>
              <label
                className="row gap-2"
                style={{
                  fontSize: 'var(--fs-2xs)',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                  marginBottom: 6,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Field
                {leaf && (
                  <span
                    style={{
                      fontSize: 'var(--fs-2xs)',
                      fontWeight: 700,
                      color: 'var(--text-muted)',
                      background: 'var(--bg-muted)',
                      borderRadius: 99,
                      padding: '1px 7px',
                      letterSpacing: 0,
                    }}
                  >
                    {Object.keys(fieldDraft).length} fields
                  </span>
                )}
              </label>
              <Select
                value={selectedLeaf}
                onChange={(e) => setSelectedLeaf(e.target.value)}
              >
                {Object.keys(fieldDraft)
                  .sort()
                  .map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
              </Select>
            </div>
          )}

          {/* Version select + inline Activate button */}
          <div>
            <label
              style={{
                fontSize: 'var(--fs-2xs)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginBottom: 6,
                display: 'block',
              }}
            >
              Version
            </label>
            <div className="row gap-2">
              <Select
                value={selectedVersionId ?? ''}
                className="grow"
                onChange={(e) => {
                  if (e.target.value === '__new') {
                    onNewVersion();
                    return;
                  }
                  setSelectedVersionId(Number(e.target.value));
                }}
              >
                {group?.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version}
                    {v.isActive ? ' · active' : ''}
                    {preview[selectedKey] === v.id ? ' · preview' : ''}
                    {v.note ? ` — ${v.note}` : ''}
                  </option>
                ))}
                <option disabled>──────────────────────────</option>
                <option value="__new">＋ New version…</option>
              </Select>
              <button
                className={
                  version?.isActive
                    ? 'btn btn-secondary btn-sm'
                    : 'btn btn-success btn-sm'
                }
                disabled={!!version?.isActive || activateStatus.isLoading}
                style={{ flexShrink: 0 }}
                title={
                  version?.isActive
                    ? 'This is already the active version'
                    : 'Make this the active version'
                }
                onClick={onActivate}
              >
                <Icon name="check" size={14} />
                Activate
              </button>
            </div>

            {/* Status badges + note */}
            {(version?.isActive || isPreviewing || version?.note) && (
              <div
                className="row gap-2"
                style={{ marginTop: 8, flexWrap: 'wrap' }}
              >
                {version?.isActive && (
                  <Badge color="green" size="sm" dot>
                    Active
                  </Badge>
                )}
                {isPreviewing && (
                  <Badge color="purple" size="sm" dot>
                    Preview
                  </Badge>
                )}
                {version?.note && (
                  <span
                    className="text-muted"
                    style={{ fontSize: 'var(--fs-xs)' }}
                  >
                    {version.note}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div
            style={{ height: 1, background: 'var(--border)', flexShrink: 0 }}
          />

          {/* EN / 中文 pill tabs */}
          <div
            className="row"
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Tabs
              pill
              active={lang}
              onChange={(id) => setLang(id as 'en' | 'cn')}
              tabs={[
                { id: 'en', label: 'English' },
                { id: 'cn', label: '中文' },
              ]}
            />
            <span className="text-faint" style={{ fontSize: 'var(--fs-2xs)' }}>
              monospace
            </span>
          </div>

          {/* Editor — full remaining height */}
          <div
            key={editorKey}
            className="col gap-3"
            style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
          >
            {renderEditor()}
          </div>
        </div>
      </Panel>

      {/* Confirm the destructive whole-DB overwrite before importing. */}
      <Modal
        open={!!pendingImport}
        onClose={() => setPendingImport(null)}
        title="Overwrite prompt DB?"
        width={460}
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              onClick={() => setPendingImport(null)}
              disabled={importStatus.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={runImport}
              loading={importStatus.isLoading}
            >
              Overwrite {pendingImport?.rows.length ?? 0} prompts
            </Button>
          </div>
        }
      >
        <Alert color="red" title="This replaces the entire prompt DB">
          Uploading <strong>{pendingImport?.fileName}</strong> will delete all{' '}
          existing matchmaker prompts and versions, then insert the{' '}
          <strong>{pendingImport?.rows.length ?? 0}</strong> row(s) from this
          file. This cannot be undone — download the current prompts first if
          you want a backup.
        </Alert>
      </Modal>
    </>
  );
};

export default PromptPanel;
